import { useState } from "react";
import { getData } from "@/lib/utils";
import api from "@/store/api";

import axios from "axios";

const useItems = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fetch Items
  const fetchItems = async ({
    companyId,
    userId,
    pageSize = 10,
    pageNumber = 1,
    withPaging = false,
  }: {
    companyId?: string;
    userId?: string;
    pageSize?: number;
    pageNumber?: number;
    withPaging?: boolean;
  }) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const response = await api.get(
        `/web/items/getAll?pageSize=${pageSize}&pageNumber=${pageNumber}&withPaging=${withPaging}${
          userId ? `&userId=${userId}` : ""
        }${companyId ? `&companyId=${companyId}` : ""}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );

      return response.data;
    } catch (err: any) {
      console.error(err);

      setError("Failed to fetch items. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const fetchItemSearch = async (
    text: string,
    companyId: string,
    pageSize: number = 10,
    pageNumber: number = 1,
    withPaging: boolean = false
  ) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const response = await api.get(
        `/web/items/getAll?pageSize=${pageSize}&pageNumber=${pageNumber}&withPaging=${withPaging}&search=${text}&companyId=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );

      return response.data;
    } catch (err: any) {
      console.error(err);

      setError("Failed to fetch items. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  // &search=in&type=Machine&companyId=
  const saveItem = async (newItem: any) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const formData = new FormData();
      // Append basic fields
      formData.append("ItemNR", newItem.ItemNR || "0");
      formData.append("name", newItem.name || "");
      if (newItem.code) formData.append("code", newItem.code);
      if (newItem.brand) formData.append("brand", newItem.brand);
      if (newItem.model) formData.append("model", newItem.model);
      formData.append("description", newItem.description || "");
      formData.append("price", newItem.price?.toString() || "0");

      formData.append("categories", newItem.categories || "");
      if (newItem.id) formData.append("id", newItem.id);
      if (newItem.vat) formData.append("vat", newItem.vat.toString());

      // Append image

      // Append attachments as flat files and send metadata separately

      const response = await api.post(`/web/items/upsert`, formData, {
        headers: {
          Authorization: `Bearer ${Token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (err: any) {
      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to save the item. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const importItems = async (item: any) => {
    const Token = getData("tokens")?.accessToken;
    try {
      const formData = new FormData();
      formData.append("excelFile", item || "");
      const response = await api.post(`/web/items/import-excel`, formData, {
        headers: {
          Authorization: `Bearer ${Token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (err) {
      console.error(err);
    }
  };

  const allMarketplaceProduct = async ({
    pageSize = 10,
    pageNumber = 1,
    storeId,
    brand,
    category,
    withPaging = false,
    search = "",
  }: {
    pageSize?: number;
    storeId?: string;
    pageNumber?: number;
    withPaging?: boolean;
    search?: string;
    brand?: string;
    category?: string;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/web/items/public/published?pageSize=${pageSize}&pageNumber=${pageNumber}&withPaging=${withPaging}${
          search ? `&search=${search}` : ""
        }${storeId ? `&storeId=${storeId}` : ""}${
          brand ? `&brand=${brand}` : ""
        }${category ? `&category=${category}` : ""}`
      );
      setIsLoading(false);

      return response.data.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }

        setError(err.response?.data?.message);
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getPublishedItem = async (id: string) => {
    try {
      const response = await api.get(`/web/items/public/published/${id}`);
      return response.data.data;
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const publishItem = async (id: string | undefined, publish: boolean) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }
      const response = await api.put(
        `/web/items/publish/${id}`,
        {
          isPublished: publish,
        },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      return response.data;
    } catch (err: any) {
      console.error(err);

      setError("Failed to publish the item. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const updateItem = async (newItem: {
    name: string;
    price: number;
    minOrder: number;
    descAr: string;
    descEn: string;
    countryOfOrigin: string;
    leadTime: string;
    attachments: File;
    pdfLink: string;
    sfdaMdma: string;
    id: string;
    unit?: string;
    descriptionEn?: string;
    descriptionAr?: string;
    images?: File[];
    categories?: string[];
    image?: File;
    type?: string;
    code?: string;
    brand?: string;
    model?: string;
    description?: string;
    video?: string;
  }) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const formData = new FormData();

      // Handle categories as categoriesIds - append each category separately
      if (newItem.categories) {
        if (Array.isArray(newItem.categories)) {
          newItem.categories.forEach((category) => {
            formData.append("categoriesIds", category);
          });
        } else {
          formData.append("categoriesIds", newItem.categories);
        }
      }

      if (newItem.code) formData.append("code", newItem.code);
      if (newItem.brand) formData.append("brand", newItem.brand);
      if (newItem.model) formData.append("model", newItem.model);
      if (newItem.minOrder)
        formData.append("minOrder", newItem.minOrder.toString());
      if (newItem.descAr) formData.append("descAr", newItem.descAr);
      if (newItem.descEn) formData.append("descEn", newItem.descEn);
      if (newItem.type) formData.append("type", newItem.type);
      if (newItem.countryOfOrigin)
        formData.append("countryOfOrigin", newItem.countryOfOrigin);
      if (newItem.leadTime) formData.append("leadTime", newItem.leadTime);
      if (newItem.attachments)
        formData.append(
          "attachments",
          newItem.attachments,
          newItem.attachments.name
        );
      if (newItem.pdfLink) formData.append("pdfLink", newItem.pdfLink);
      if (newItem.sfdaMdma) formData.append("sfdaMdma", newItem.sfdaMdma);
      if (newItem?.id) formData.append("id", newItem.id);
      if (newItem.name) formData.append("name", newItem.name);
      if (newItem.price) formData.append("price", newItem.price.toString());
      if (newItem?.unit) formData.append("unit", newItem.unit);
      if (newItem?.descriptionEn)
        formData.append("descriptionEn", newItem.descriptionEn);
      if (newItem.descriptionAr)
        formData.append("descriptionAr", newItem.descriptionAr);
      // if (newItem.image instanceof File) {
      //   formData.append("image", newItem.image, newItem.image.name);
      // }
      // Handle video if present
      if (newItem.video) {
        formData.append("video", newItem.video);
      }

      // Handle images with size check and compression
      if (newItem.image) {
        formData.append("images", newItem.image);
      }
      if (newItem.images && newItem.images.length > 0) {
        for (const file of newItem.images) {
          // Check file size (5MB limit)
          if (file.size > 5 * 1024 * 1024) {
            throw new Error(
              `Image ${file.name} is too large. Maximum size is 5MB`
            );
          }

          // Check file type
          // if (!file.type.startsWith("image/")) {
          //   throw new Error(`File ${file.name} is not an image`);
          // }

          formData.append("images", file);
        }
      }

      const response = await api.post(`/web/items/upsert`, formData, {
        headers: {
          Authorization: `Bearer ${Token}`,
          "Content-Type": "multipart/form-data",
        },
        // Add timeout configuration
        timeout: 30000, // 30 seconds timeout
        // Add upload progress tracking
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total!
          );
          console.log(`Upload Progress: ${percentCompleted}%`);
        },
      });
      return response.data;
    } catch (err: any) {
      console.error(err);

      // Handle specific error cases
      if (err.code === "ECONNABORTED" || err.message.includes("timeout")) {
        setError(
          "Request timed out. Please try again with smaller images or check your internet connection."
        );
      } else if (err.message.includes("too large")) {
        setError(err.message);
      } else if (err.message.includes("not an image")) {
        setError(err.message);
      } else {
        setError(
          err.response?.data?.message ||
            err.response?.data?.details ||
            "Failed to save the item. Please try again."
        );
      }
      throw err; // Re-throw to handle in the component
    } finally {
      setIsLoading(false);
    }
  };
  const deleteItem = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }
      const response = await api.delete(`/web/items/delete?id=${id}`, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      return response.data;
    } catch (err: any) {
      setError("Failed to delete item.");
    } finally {
      setIsLoading(false);
    }
  };
  const suggestions = async (search: string) => {
    try {
      const response = await api.get(
        `/web/items/public/suggestions?search=${search}`
      );
      return response.data;
    } catch {
      setError("Failed to fetch suggestions.");
    }
  };
  const fetchItem = async (id: string) => {
    try {
      setIsLoading(true);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const response = await api.get(`/web/items/get?id=${id}`, {
        headers: { Authorization: `Bearer ${Token}` },
      });

      return response.data;
    } catch (err: any) {
      setError("Failed to fetch the item. Please try again.");
      console.error(err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };
  return {
    fetchItems,
    fetchItemSearch,
    saveItem,
    publishItem,
    getPublishedItem,
    deleteItem,
    fetchItem,
    isLoading,
    error,
    importItems,
    allMarketplaceProduct,
    suggestions,
    updateItem,
  };
};
export default useItems;
